import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import React, {
  memo, useCallback, useMemo, useState
} from 'react';
import { AiOutlineLoading } from 'react-icons/ai';
import { FiBook, FiCopy, FiDownload } from 'react-icons/fi';

import { Icon } from '@/components/icon/icon';

import styles from '../explorer.module.scss';
import { QueryResult } from '../types';
import { ROWS_PER_PAGE } from '../utils/constants';
import { buildCsv } from '../utils/csv';
import { generateNotebook } from '../utils/notebookExport';
import ProfilePopover from './ProfilePopover';


type Props = {
  result: QueryResult | null;
  isExecuting?: boolean;
  sql?: string;
};

function ResultsGrid( { result, isExecuting, sql }: Props ) {
  const [ sorting, setSorting ] = useState<SortingState>( [] );
  const [ copyFeedback, setCopyFeedback ] = useState( false );
  const [ profileState, setProfileState ] = useState<{ col: string; anchor: HTMLElement } | null>( null );
  const [ currentPage, setCurrentPage ] = useState( 1 );

  // Reset page when result changes
  React.useEffect( () => {
    setCurrentPage( 1 );
  }, [ result ] );

  const columnHelper = useMemo( () => createColumnHelper<Record<string, unknown>>(), [] );

  const columns = useMemo( () => {
    if ( !result || result.columns.length === 0 ) return [];

    return result.columns.map( col => columnHelper.accessor( col, {
      header: col,
      cell: info => {
        const val = info.getValue();
        if ( val === null || val === undefined ) return <span className={ styles.nullValue }>NULL</span>;
        if ( typeof val === 'object' ) return JSON.stringify( val );
        return String( val );
      }
    } )
    );
  }, [ result, columnHelper ] );

  const table = useReactTable( {
    data: result?.rows || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  } );

  const handleCopy = useCallback( () => {
    if ( !result || result.rows.length === 0 ) return;
    const csv = buildCsv( result.columns, result.rows );
    navigator.clipboard.writeText( csv ).then( () => {
      setCopyFeedback( true );
      setTimeout( () => setCopyFeedback( false ), 2000 );
    } ).catch( ( err ) => {
      console.warn( 'Clipboard write failed:', err );
    } );
  }, [ result ] );

  const handleExportCsv = useCallback( () => {
    if ( !result || result.rows.length === 0 ) return;
    const csv = buildCsv( result.columns, result.rows );
    const blob = new Blob( [ csv ], { type: 'text/csv;charset=utf-8;' } );
    const url = URL.createObjectURL( blob );
    const a = document.createElement( 'a' );
    a.href = url;
    a.download = `query_results_${ new Date().toISOString().slice( 0, 19 ).replace( /[:.]/g, '-' ) }.csv`;
    a.click();
    URL.revokeObjectURL( url );
  }, [ result ] );

  const handleExportNotebook = useCallback( () => {
    if ( !result || result.rows.length === 0 || !sql ) return;
    const nb = generateNotebook( sql, result );
    const blob = new Blob( [ nb ], { type: 'application/json;charset=utf-8;' } );
    const url = URL.createObjectURL( blob );
    const a = document.createElement( 'a' );
    a.href = url;
    a.download = `query_${ new Date().toISOString().slice( 0, 19 ).replace( /[:.]/g, '-' ) }.ipynb`;
    a.click();
    URL.revokeObjectURL( url );
  }, [ result, sql ] );

  const handleProfileClick = useCallback( ( col: string, e: React.MouseEvent ) => {
    e.stopPropagation(); // Don't trigger sort
    setProfileState( prev => ( prev?.col === col ? null : { col, anchor: e.currentTarget as HTMLElement } )
    );
  }, [] );

  const handleCloseProfile = useCallback( () => {
    setProfileState( null );
  }, [] );

  if ( isExecuting ) {
    return (
      <div className={ styles.resultsEmpty }>
        <Icon icon={ AiOutlineLoading } className={ styles.treeSpinner } />
        <span>Running query...</span>
      </div>
    );
  }

  if ( !result ) {
    return (
      <div className={ styles.resultsEmpty }>
        <span>Run a query to see results</span>
      </div>
    );
  }

  if ( result.error ) {
    return (
      <div className={ styles.resultsError }>
        <span>{ result.error }</span>
      </div>
    );
  }

  return (
    <div className={ styles.resultsGrid } data-testid="results-grid">
      <div className={ styles.resultsTableWrapper }>
        <table className={ styles.resultsTable } data-testid="results-table">
          <thead>
            { table.getHeaderGroups().map( headerGroup => (
              <tr key={ headerGroup.id }>
                <th>#</th>
                { headerGroup.headers.map( header => (
                  <th
                    key={ header.id }
                    onClick={ ( e ) => {
                      // Don't sort if the profile button was clicked
                      if ( ( e.target as HTMLElement ).closest( '[data-profile-btn]' ) ) return;
                      const sortHandler = header.column.getToggleSortingHandler();
                      if ( sortHandler ) sortHandler( e );
                    } }
                    className={ clsx( header.column.getCanSort() && styles.sortableHeader ) }
                  >
                    <span className={ styles.headerContent }>
                      <span>
                        { flexRender( header.column.columnDef.header, header.getContext() ) }
                        { { asc: ' \u25B2', desc: ' \u25BC' }[header.column.getIsSorted() as string] ?? '' }
                      </span>
                      <button
                        type="button"
                        className={ styles.profileBtn }
                        data-profile-btn
                        data-testid={ `profile-btn-${ header.id }` }
                        onClick={ ( e ) => handleProfileClick( header.id, e ) }
                        title={ `Profile: ${ header.id }` }
                      >
                        i
                      </button>
                    </span>
                  </th>
                ) ) }
              </tr>
            ) ) }
          </thead>
          <tbody>
            { ( () => {
              const allRows = table.getRowModel().rows;
              const start = ( currentPage - 1 ) * ROWS_PER_PAGE;
              const end = start + ROWS_PER_PAGE;
              return allRows.slice( start, end ).map( ( row, idx ) => (
                <tr key={ row.id }>
                  <td>{ start + idx + 1 }</td>
                  { row.getVisibleCells().map( cell => (
                    <td key={ cell.id }>
                      { flexRender( cell.column.columnDef.cell, cell.getContext() ) }
                    </td>
                  ) ) }
                </tr>
              ) );
            } )() }
          </tbody>
        </table>
      </div>
      { ( () => {
        const totalRows = table.getRowModel().rows.length;
        const totalPages = Math.ceil( totalRows / ROWS_PER_PAGE );
        if ( totalPages <= 1 ) return null;

        // Show max 7 page buttons with ellipsis
        const pages: ( number | '...' )[] = [];
        if ( totalPages <= 7 ) {
          for ( let i = 1; i <= totalPages; i++ ) pages.push( i );
        } else {
          pages.push( 1 );
          if ( currentPage > 3 ) pages.push( '...' );
          for ( let i = Math.max( 2, currentPage - 1 ); i <= Math.min( totalPages - 1, currentPage + 1 ); i++ ) {
            pages.push( i );
          }
          if ( currentPage < totalPages - 2 ) pages.push( '...' );
          pages.push( totalPages );
        }

        return (
          <div className={ styles.resultsPagination }>
            <button
              type="button"
              data-testid="results-prev-btn"
              className={ styles.resultsPaginationBtn }
              disabled={ currentPage === 1 }
              onClick={ () => setCurrentPage( p => p - 1 ) }
            >
              Prev
            </button>
            { pages.map( ( p, i ) => ( p === '...' ? (
              <span key={ `ellipsis-${ i }` } className={ styles.resultsPaginationEllipsis }>...</span>
            ) : (
              <button
                key={ p }
                type="button"
                className={ clsx( styles.resultsPaginationBtn, p === currentPage && styles.resultsPaginationBtn_active ) }
                onClick={ () => setCurrentPage( p as number ) }
              >
                { p }
              </button>
            ) )
            ) }
            <button
              type="button"
              data-testid="results-next-btn"
              className={ styles.resultsPaginationBtn }
              disabled={ currentPage === totalPages }
              onClick={ () => setCurrentPage( p => p + 1 ) }
            >
              Next
            </button>
          </div>
        );
      } )() }
      <div className={ styles.resultsStatusBar }>
        <span>
          { result.rows.length > ROWS_PER_PAGE
            ? `Page ${ currentPage } of ${ Math.ceil( result.rows.length / ROWS_PER_PAGE ) } (${ result.rows.length.toLocaleString() } rows)`
            : `${ result.rows.length.toLocaleString() } row${ result.rows.length !== 1 ? 's' : '' }` }
          { result.executionTime !== undefined && ` \u2014 ${ result.executionTime.toFixed( 2 ) }s` }
        </span>
        <div className={ styles.resultsActions }>
          <button
            type="button"
            data-testid="copy-results-btn"
            className={ styles.resultsActionBtn }
            onClick={ handleCopy }
            title="Copy as CSV"
          >
            <FiCopy size={ 12 } />
            { copyFeedback ? 'Copied!' : 'Copy' }
          </button>
          <button
            type="button"
            data-testid="export-csv-btn"
            className={ styles.resultsActionBtn }
            onClick={ handleExportCsv }
            title="Export as CSV"
          >
            <FiDownload size={ 12 } />
            Export CSV
          </button>
          <button
            type="button"
            data-testid="export-notebook-btn"
            className={ styles.resultsActionBtn }
            onClick={ handleExportNotebook }
            title="Export as Notebook"
            disabled={ !sql }
          >
            <FiBook size={ 12 } />
            Export Notebook
          </button>
        </div>
      </div>
      { profileState && result && (
        <ProfilePopover
          columnName={ profileState.col }
          result={ result }
          anchorEl={ profileState.anchor }
          onClose={ handleCloseProfile }
        />
      ) }
    </div>
  );
}

export default memo( ResultsGrid );
